import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { formatDate, extractNumericId } from '../../utils/formatters';
import useWarranty from '../../hooks/useWarranty';
import useCustomer from '../../hooks/useCustomer';
import useRepair from '../../../hooks/useRepair';
import Card from '../../common/Card';
import Button from '../../common/Button';
import StatusBadge from '../../common/StatusBadge';
import Loading from '../../common/Loading';
import ErrorMessage from '../../common/ErrorMessage';
import ConfirmDialog from '../../common/ConfirmDialog';
import FormTextarea from '../../common/FormTextarea';
import FormSelect from '../../common/FormSelect';
import FormDatePicker from '../../common/FormDatePicker';
import RepairList from '../../repair/RepairList';
import RepairForm from '../../repair/RepairForm';
import './styles.css';

/**
 * 保養單詳情組件
 * 用於顯示保養單詳細信息
 * 
 * @param {Object} props - 組件屬性
 * @param {string} props.token - 認證令牌
 * @param {Object} props.user - 用戶信息
 * @returns {JSX.Element} 保養單詳情組件
 */
function WarrantyDetail({ token, user }) {
    const { warrantyId } = useParams();
    const navigate = useNavigate();

    // 使用自定義Hook
    const {
        warranties,
        loading,
        error,
        updateStatus,
        updateLoading,
        updateError
    } = useWarranty(token);

    const { getCustomerById } = useCustomer(token);

    const { fetchRepairsByWarrantyId } = useRepair(token);

    // 本地狀態
    const [warranty, setWarranty] = useState(null);
    const [customer, setCustomer] = useState(null);
    const [customerLoading, setCustomerLoading] = useState(false);
    const [notes, setNotes] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('');
    const [warrantyExpirationDate, setWarrantyExpirationDate] = useState('');
    const [confirmDialog, setConfirmDialog] = useState({
        isOpen: false,
        action: null,
        title: '',
        message: ''
    });

    // 添加維修單相關狀態
    const [showRepairForm, setShowRepairForm] = useState(false);
    const [repairs, setRepairs] = useState([]);
    const [repairsLoading, setRepairsLoading] = useState(false);
    const [repairsError, setRepairsError] = useState(null);

    // 保養單狀態選項
    const statusOptions = [
        { value: 'Pending', label: '待處理' },
        { value: 'Approved', label: '已批准' },
        { value: 'Rejected', label: '已拒絕' }
    ];

    // 從產品名稱中提取客戶ID
    const extractCustomerIdFromProductName = (productName) => {
        if (!productName) return null;

        // 嘗試從產品名稱中提取括號內的客戶ID
        const match = productName.match(/\((\d+)\)/);
        if (match && match[1]) {
            // console.log('從產品名稱中提取到客戶ID:', match[1]);
            return match[1];
        }

        return null;
    };

    // 將 Shopify MediaImage 格式轉換為標準的 CDN URL
    const convertMediaImageToCdnUrl = (mediaImageUrl) => {
        if (!mediaImageUrl || !mediaImageUrl.startsWith('gid://shopify/MediaImage/')) {
            return null;
        }

        // 從 gid 中提取 MediaImage ID
        const mediaId = mediaImageUrl.split('/').pop();
        // console.log('從 Shopify MediaImage gid 中提取 ID:', mediaId);

        // 嘗試多種可能的 Shopify CDN URL 格式
        const shopId = '0919/7335/3791'; // 使用示例中的商店 ID

        // 嘗試使用 mediaId 作為文件名的多種格式
        const possibleFormats = [
            `https://cdn.shopify.com/s/files/1/${shopId}/files/${mediaId}.png`,
            `https://cdn.shopify.com/s/files/1/${shopId}/files/${mediaId}.jpg`,
            `https://cdn.shopify.com/s/files/1/${shopId}/files/${mediaId}.jpeg`,
            `https://cdn.shopify.com/s/files/1/${shopId}/products/${mediaId}.png`,
            `https://cdn.shopify.com/s/files/1/${shopId}/images/${mediaId}.png`
        ];

        // 返回第一個可能的格式，前端會嘗試加載，如果失敗會顯示錯誤處理
        // console.log('構建的 Shopify CDN URL 格式:', possibleFormats[0]);
        return possibleFormats[0];
    };

    // 獲取直接的 Shopify URL（與用戶提供的示例格式相同）
    const getDirectShopifyUrl = (mediaId) => {
        // 直接使用用戶提供的示例 URL 格式，但替換文件名部分
        return `https://cdn.shopify.com/s/files/1/0919/7335/3791/files/${mediaId}.png?v=1741707396`;
    };

    // 獲取 Shopify Admin API URL（用於後端獲取圖片）
    const getShopifyAdminApiUrl = (mediaId) => {
        // 使用 /api/upload/file 端點來獲取圖片
        return `/api/upload/file?id=${encodeURIComponent(`gid://shopify/MediaImage/${mediaId}`)}`;
    };

    // 獲取保養單數據
    useEffect(() => {
        if (!loading && warranties.length > 0) {
            const foundWarranty = warranties.find(w =>
                w.id === warrantyId ||
                w.id.split('/').pop() === warrantyId
            );

            if (foundWarranty) {
                setNotes(foundWarranty.verification_notes);
                setWarranty(foundWarranty);
                setSelectedStatus(foundWarranty.verification_status);
                setWarrantyExpirationDate(foundWarranty.warranty_expiration_date || '');
                console.log('找到保養單數據:', foundWarranty);

                // 獲取客戶數據
                const loadCustomer = async () => {
                    setCustomerLoading(true);
                    try {
                        // 首先嘗試從保養單的customerId獲取客戶數據
                        let customerId = null;

                        if (foundWarranty.customerId) {
                            customerId = extractNumericId(foundWarranty.customerId);
                            // console.log('從保養單customerId獲取客戶ID:', customerId);
                        }

                        // 如果保養單中沒有customerId，嘗試從產品名稱中提取
                        if (!customerId && foundWarranty.product_name) {
                            customerId = extractCustomerIdFromProductName(foundWarranty.product_name);
                            // console.log('從產品名稱中提取客戶ID:', customerId);
                        }

                        if (customerId) {
                            // console.log('嘗試獲取客戶數據，ID:', customerId);
                            const customerData = await getCustomerById(customerId);
                            // console.log('獲取到客戶數據:', customerData);
                            setCustomer(customerData);
                        } else {
                            console.error('無法從保養單中獲取客戶ID');
                        }
                    } catch (err) {
                        console.error('獲取客戶數據失敗:', err);
                    } finally {
                        setCustomerLoading(false);
                    }
                };

                loadCustomer();

                // 獲取相關的維修單
                const loadRepairs = async () => {
                    setRepairsLoading(true);
                    try {
                        const fullWarrantyId = foundWarranty.id;
                        const repairData = await fetchRepairsByWarrantyId(fullWarrantyId);
                        setRepairs(repairData || []);

                        setRepairsError(null);
                    } catch (error) {
                        console.error('獲取維修單時出錯:', error);
                        setRepairsError('獲取維修單時出錯');
                    } finally {
                        setRepairsLoading(false);
                    }
                };

                loadRepairs();
            } else {
                console.error('未找到保養單數據，ID:', warrantyId);
            }
        }
    }, [loading, warranties, warrantyId, getCustomerById, fetchRepairsByWarrantyId]);

    // 顯示確認對話框
    const showConfirmDialog = (action, title, message) => {
        setConfirmDialog({
            isOpen: true,
            action,
            title,
            message
        });
    };

    // 關閉確認對話框
    const closeConfirmDialog = () => {
        setConfirmDialog({
            isOpen: false,
            action: null,
            title: '',
            message: ''
        });
    };

    // 處理狀態選擇變更
    const handleStatusChange = (e) => {
        setSelectedStatus(e.target.value);
    };

    // 處理狀態更新提交
    const handleStatusSubmit = () => {
        // 允許即使狀態相同也可以更新備註或保修到期日期
        showConfirmDialog(
            'update',
            '更新保養單',
            `您確定要更新此保養單嗎？${selectedStatus !== warranty.verification_status ?
                `\n狀態將從「${getStatusLabel(warranty.verification_status)}」更新為「${getStatusLabel(selectedStatus)}」` :
                ''}${warrantyExpirationDate !== (warranty.warranty_expiration_date || '') ?
                    `\n保修到期日期將更新為「${formatDate(warrantyExpirationDate)}」` :
                    ''}`
        );
    };

    // 獲取狀態標籤
    const getStatusLabel = (statusValue) => {
        const option = statusOptions.find(opt => opt.value === statusValue);
        return option ? option.label : statusValue;
    };

    // 處理狀態更新
    const handleStatusUpdate = async (newStatus) => {
        // console.log(`開始更新保養單狀態: ID=${warranty.id}, 新狀態=${newStatus}, 備註=${notes}, 保修到期日期=${warrantyExpirationDate}`);
        try {
            // 獲取當前日期作為驗證日期
            const currentDate = new Date().toISOString().split('T')[0];

            const updateData = {
                status: newStatus,
                notes: notes,
                warranty_expiration_date: warrantyExpirationDate || null,
                verification_date: currentDate
            };

            const success = await updateStatus(warranty.id, updateData);
            // console.log(`保養單狀態更新結果: ${success ? '成功' : '失敗'}`);

            if (success === true) {
                // 使用函數式更新，確保不會嘗試渲染一個對象
                setWarranty(prev => {
                    if (!prev) return null;

                    // 創建一個新的對象，而不是直接修改原對象
                    const updatedWarranty = {
                        ...prev,
                        verification_status: newStatus,
                        verification_notes: notes,
                        warranty_expiration_date: warrantyExpirationDate || null,
                        verification_date: currentDate
                    };

                    return updatedWarranty;
                });

                setNotes('');
                closeConfirmDialog();
                // console.log('保養單狀態已更新，本地狀態已更新');

                // 顯示成功消息
                alert(`保養單已成功更新`);

                // 重新加載頁面以確保所有狀態都是最新的
                setTimeout(() => {
                    window.location.reload();
                }, 1000);
            } else {
                console.error('保養單狀態更新失敗');

                // 顯示錯誤消息
                if (updateError) {
                    alert(`保養單更新失敗: ${updateError}`);
                } else {
                    alert('保養單更新失敗，請稍後再試');
                }
            }
        } catch (error) {
            console.error('保養單狀態更新過程中發生錯誤:', error);
            alert(`保養單更新過程中發生錯誤: ${error.message || '未知錯誤'}`);
        }
    };

    // 處理確認操作
    const handleConfirmAction = async () => {
        const { action } = confirmDialog;

        if (action === 'update') {
            try {
                await handleStatusUpdate(selectedStatus);
                // 成功處理已經在 handleStatusUpdate 中完成
            } catch (error) {
                console.error('確認操作過程中發生錯誤:', error);
                // 錯誤處理已經在 handleStatusUpdate 中完成
            }
        }
    };

    // 返回上一頁
    const handleBack = () => {
        navigate('/warranties');
    };

    // 處理維修表單提交的回調
    const handleRepairFormSuccess = (repair) => {
        setShowRepairForm(false);
        
        // 如果成功創建了維修單，刷新維修單列表
        if (repair) {
            setRepairsLoading(true);
            fetchRepairsByWarrantyId(warranty.id)
                .then(data => {
                    setRepairs(data || []);
                    setRepairsError(null);
                })
                .catch(err => {
                    console.error('刷新維修單時出錯:', err);
                    setRepairsError('刷新維修單時出錯');
                })
                .finally(() => {
                    setRepairsLoading(false);
                });
        }
    };

    // 加載中狀態
    if (loading) {
        return <Loading text="正在加載保養單數據..." />;
    }

    // 錯誤狀態
    if (error) {
        return (
            <ErrorMessage
                message={error}
                onRetry={() => window.location.reload()}
            />
        );
    }

    // 未找到保養單
    if (!warranty) {
        return (
            <ErrorMessage
                message="找不到保養單數據"
                onRetry={() => navigate('/warranties')}
            />
        );
    }

    // 處理收據圖片URL
    const getReceiptImageUrl = (receiptUrl) => {
        if (!receiptUrl) return null;

        // 處理 Shopify MediaImage 格式的 URL (gid://shopify/MediaImage/xxxxx)
        if (receiptUrl.startsWith('gid://shopify/MediaImage/')) {
            // 使用 /api/upload/file 端點來獲取圖片
            const mediaId = receiptUrl.split('/').pop();
            const uploadFileUrl = `/api/upload/file?id=${encodeURIComponent(receiptUrl)}`;
            // console.log('使用 /api/upload/file 端點獲取 Shopify MediaImage:', uploadFileUrl);
            return uploadFileUrl;
        }

        // 如果已經是 Shopify CDN URL 格式，直接返回
        if (receiptUrl.includes('cdn.shopify.com/s/files/')) {
            // console.log('已經是 Shopify CDN URL 格式:', receiptUrl);
            return receiptUrl;
        }

        // 檢查URL是否為相對路徑或絕對路徑
        if (!receiptUrl.startsWith('http://') && !receiptUrl.startsWith('https://')) {
            // 如果是相對路徑，添加完整的域名
            const baseUrl = window.location.origin;
            // 確保路徑格式正確
            const formattedPath = receiptUrl.startsWith('/') ? receiptUrl : `/${receiptUrl}`;
            // console.log('處理收據圖片URL:', `${baseUrl}${formattedPath}`);
            return `${baseUrl}${formattedPath}`;
        }

        // 如果已經是完整URL，直接返回
        // console.log('收據圖片URL已經是完整路徑:', receiptUrl);
        return receiptUrl;
    };

    // 獲取 Shopify 圖片的可能 URL 格式
    const getShopifyImageFormats = (mediaId) => {
        const shopIds = ['0919/7335/3791'];
        const formats = [];

        // 添加 /api/upload/file 路徑（首選方式）
        const gid = `gid://shopify/MediaImage/${mediaId}`;
        formats.push(`/api/upload/file?id=${encodeURIComponent(gid)}`);

        // 使用用戶提供的示例 URL 格式
        formats.push(`https://cdn.shopify.com/s/files/1/0919/7335/3791/files/${mediaId}.png?v=1741707396`);
        formats.push(`https://cdn.shopify.com/s/files/1/0919/7335/3791/files/${mediaId}.png`);

        // 為每個商店 ID 生成不同的 URL 格式
        shopIds.forEach(shopId => {
            // 不同路徑的嘗試
            formats.push(`https://cdn.shopify.com/s/files/1/${shopId}/products/${mediaId}.png`);
            formats.push(`https://cdn.shopify.com/s/files/1/${shopId}/images/${mediaId}.png`);
            formats.push(`https://cdn.shopify.com/s/files/1/${shopId}/files/${mediaId}.jpg`);
            formats.push(`https://cdn.shopify.com/s/files/1/${shopId}/files/${mediaId}.jpeg`);

            // 添加不同尺寸格式
            formats.push(`https://cdn.shopify.com/s/files/1/${shopId}/files/${mediaId}_large.png`);
            formats.push(`https://cdn.shopify.com/s/files/1/${shopId}/files/${mediaId}_medium.png`);
            formats.push(`https://cdn.shopify.com/s/files/1/${shopId}/files/${mediaId}_small.png`);
        });

        // 添加備用圖片
        formats.push(`https://cdn.shopify.com/shopifycloud/shopify/assets/no-image-2048-5e88c1b20e087fb7bbe9a3771824e743c244f437e4f8ba93bbf7b11b53f7824c.gif`);

        return formats;
    };

    // 管理員操作卡片
    const renderAdminActions = () => {
        if (user?.role !== 'admin') return null;

        return (
            <Card
                title="管理員操作"
                className="warranty-detail-card"
            >
                <div className="warranty-detail-admin-form">
                    <FormSelect
                        label="狀態"
                        value={selectedStatus}
                        onChange={handleStatusChange}
                        options={statusOptions}
                        className="warranty-detail-status-select"
                    />

                    <FormDatePicker
                        id="warrantyExpirationDate"
                        label="保修到期日期"
                        value={warrantyExpirationDate}
                        onChange={(e) => setWarrantyExpirationDate(e.target.value)}
                        className="warranty-detail-expiration-date"
                    />

                    <FormTextarea
                        label="備註"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="輸入備註..."
                    />
                </div>

                <div className="warranty-detail-admin-actions">
                    <Button
                        variant="primary"
                        onClick={handleStatusSubmit}
                        disabled={updateLoading || (selectedStatus === warranty.verification_status && !notes && warrantyExpirationDate === (warranty.warranty_expiration_date || ''))}
                    >
                        {updateLoading ? '更新中...' : '更新保養單'}
                    </Button>
                </div>
            </Card>
        );
    };

    return (
        <div className="warranty-detail-container">
            {/* 頁面標題 */}
            <div className="warranty-detail-header">
                <Button
                    variant="outline"
                    size="small"
                    onClick={handleBack}
                    className="warranty-detail-back-button"
                >
                    返回
                </Button>
                <h2 className="warranty-detail-title">保養單詳情</h2>
            </div>

            {/* 更新錯誤提示 */}
            {updateError && (
                <ErrorMessage message={updateError} />
            )}

            {/* 保養單信息卡片 */}
            <Card
                title="保養單信息"
                className="warranty-detail-card"
            >
                <div className="warranty-detail-info">
                    <div className="warranty-detail-row">
                        <div className="warranty-detail-label">保養單ID:</div>
                        <div className="warranty-detail-value">{extractNumericId(warranty.id)}</div>
                    </div>

                    <div className="warranty-detail-row">
                        <div className="warranty-detail-label">產品名稱:</div>
                        <div className="warranty-detail-value">{warranty.product_name}</div>
                    </div>

                    <div className="warranty-detail-row">
                        <div className="warranty-detail-label">購買日期:</div>
                        <div className="warranty-detail-value">{formatDate(warranty.purchase_date)}</div>
                    </div>

                    {warranty.warranty_expiration_date && (
                        <div className="warranty-detail-row">
                            <div className="warranty-detail-label">保修到期日期:</div>
                            <div className="warranty-detail-value">{formatDate(warranty.warranty_expiration_date)}</div>
                        </div>
                    )}

                    <div className="warranty-detail-row">
                        <div className="warranty-detail-label">狀態:</div>
                        <div className="warranty-detail-value">
                            <StatusBadge status={warranty.verification_status} />
                        </div>
                    </div>

                    {warranty.verification_notes && (
                        <div className="warranty-detail-row">
                            <div className="warranty-detail-label">備註:</div>
                            <div className="warranty-detail-value">{warranty.verification_notes}</div>
                        </div>
                    )}

                    {warranty.verification_date && (
                        <div className="warranty-detail-row">
                            <div className="warranty-detail-label">驗證日期:</div>
                            <div className="warranty-detail-value">{formatDate(warranty.verification_date)}</div>
                        </div>
                    )}

                    {warranty.staff_name && (
                        <div className="warranty-detail-row">
                            <div className="warranty-detail-label">驗證人員:</div>
                            <div className="warranty-detail-value">{warranty.staff_name}</div>
                        </div>
                    )}
                </div>
            </Card>

            {/* 客戶信息卡片 */}
            <Card
                title="客戶信息"
                className="warranty-detail-card"
            >
                {customerLoading ? (
                    <Loading text="正在加載客戶數據..." />
                ) : customer ? (
                    <div className="warranty-detail-info">
                        <div className="warranty-detail-row">
                            <div className="warranty-detail-label">姓名:</div>
                            <div className="warranty-detail-value">
                                {customer.firstName} {customer.lastName}
                            </div>
                        </div>

                        <div className="warranty-detail-row">
                            <div className="warranty-detail-label">電子郵件:</div>
                            <div className="warranty-detail-value">{customer.email}</div>
                        </div>

                        <div className="warranty-detail-row">
                            <div className="warranty-detail-label">電話:</div>
                            <div className="warranty-detail-value">{customer.phone}</div>
                        </div>

                        {(customer.address1 || customer.city || customer.province) && (
                            <div className="warranty-detail-row">
                                <div className="warranty-detail-label">地址:</div>
                                <div className="warranty-detail-value">
                                    {customer.address1}
                                    {customer.address2 && <span>, {customer.address2}</span>}
                                    {(customer.address1 || customer.address2) && customer.city && <br />}
                                    {customer.city && <span>{customer.city}</span>}
                                    {customer.city && customer.province && <span>, </span>}
                                    {customer.province && <span>{customer.province}</span>}
                                    {(customer.city || customer.province) && customer.postalCode && <span>, </span>}
                                    {customer.postalCode && <span>{customer.postalCode}</span>}
                                </div>
                            </div>
                        )}

                        <div className="warranty-detail-actions">
                            <Link
                                to={`/customers/${extractNumericId(customer.id)}`}
                                className="warranty-detail-link"
                            >
                                查看客戶詳情
                            </Link>
                        </div>
                    </div>
                ) : (
                    <div className="warranty-detail-empty">
                        <p>無法加載客戶數據</p>
                        <p className="warranty-detail-error-hint">
                            客戶ID: {
                                warranty.customerId
                                    ? extractNumericId(warranty.customerId)
                                    : extractCustomerIdFromProductName(warranty.product_name) || '未知'
                            }
                        </p>
                        <Button
                            variant="outline"
                            size="small"
                            onClick={() => {
                                setCustomerLoading(true);
                                // 嘗試從產品名稱中提取客戶ID
                                const customerId = warranty.customerId
                                    ? extractNumericId(warranty.customerId)
                                    : extractCustomerIdFromProductName(warranty.product_name);

                                if (customerId) {
                                    getCustomerById(customerId)
                                        .then(data => {
                                            setCustomer(data);
                                            // console.log('重新獲取客戶數據成功:', data);
                                        })
                                        .catch(err => {
                                            console.error('重新獲取客戶數據失敗:', err);
                                        })
                                        .finally(() => {
                                            setCustomerLoading(false);
                                        });
                                } else {
                                    console.error('無法獲取客戶ID');
                                    setCustomerLoading(false);
                                }
                            }}
                        >
                            重新加載
                        </Button>
                    </div>
                )}
            </Card>

 {/* 收據圖片卡片 */}
 {warranty.receipt_photo && (
                <Card
                    title="收據圖片"
                    className="warranty-detail-card"
                >
                    <div className="warranty-detail-receipt">
                        {warranty.receipt_photo.startsWith('gid://shopify/MediaImage/') ? (
                            // 對於 Shopify MediaImage，使用改進的顯示方式
                            <div className="warranty-detail-receipt-placeholder">
                                {/* 使用 /api/upload/file 路徑 */}
                                <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                                    <a
                                        href={`/api/upload/file?id=${encodeURIComponent(warranty.receipt_photo)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                            display: 'inline-block',
                                            padding: '10px 15px',
                                            backgroundColor: '#4CAF50',
                                            color: 'white',
                                            textDecoration: 'none',
                                            borderRadius: '4px',
                                            fontWeight: 'bold',
                                            margin: '10px 0'
                                        }}
                                    >
                                        點擊查看圖片
                                    </a>
                                </div>

                                <img
                                    src={getReceiptImageUrl(warranty.receipt_photo)}
                                    alt="收據圖片"
                                    className="warranty-detail-receipt-image"
                                    onError={(e) => {
                                        console.error('Shopify MediaImage 格式圖片加載失敗:', e);
                                        e.target.onerror = null;
                                        // 顯示佔位符
                                        e.target.style.display = 'none';
                                        // 顯示錯誤信息
                                        const placeholder = document.createElement('div');
                                        placeholder.innerHTML = `
                                            <div style="
                                                width: 100%;
                                                max-width: 400px;
                                                height: 200px;
                                                background-color: #f5f5f5;
                                                display: flex;
                                                flex-direction: column;
                                                justify-content: center;
                                                align-items: center;
                                                margin: 0 auto;
                                                border-radius: 4px;
                                                padding: 20px;
                                                text-align: center
                                            ">
                                                <p style="margin-bottom: 10px">無法直接顯示 Shopify MediaImage 格式的圖片</p>
                                                <p style="font-size: 0.9em; color: #666">請點擊上方的"點擊查看圖片"按鈕</p>
                                            </div>
                                        `;
                                        e.target.parentNode.appendChild(placeholder);
                                    }}
                                    style={{ maxWidth: '100%', maxHeight: '400px', margin: '0 auto', display: 'block' }}
                                />
                            </div>
                        ) : (
                            // 對於其他格式的圖片，正常顯示
                            <img
                                src={getReceiptImageUrl(warranty.receipt_photo)}
                                alt="收據圖片"
                                className="warranty-detail-receipt-image"
                                onError={(e) => {
                                    console.error('收據圖片加載失敗:', e);
                                    e.target.onerror = null;
                                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTQiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIiBmaWxsPSIjOTk5Ij7lm77niYfliqDovb3lpLHotKU8L3RleHQ+PC9zdmc+';
                                    e.target.style.opacity = '0.7';
                                }}
                                style={{ maxWidth: '100%', maxHeight: '400px' }}
                            />
                        )}
                    </div>
                    {/* <div className="warranty-detail-receipt-actions">
                        {warranty.receipt_photo.startsWith('gid://shopify/MediaImage/') ? (
                            <a
                                href={`/api/upload/file?id=${encodeURIComponent(warranty.receipt_photo)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="warranty-detail-link"
                            >
                                在新視窗中開啟圖片
                            </a>
                        ) : (
                            <a
                                href={getReceiptImageUrl(warranty.receipt_photo)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="warranty-detail-link"
                            >
                                在新視窗中開啟圖片
                            </a>
                        )}
                    </div> */}
                </Card>
            )}

            {/* 客戶信息卡片之後添加維修單管理卡片 */}
            <Card
                className="warranty-detail-card warranty-repair-card"
            >
                <div className="warranty-repair-header">
                    <h3 className="warranty-repair-title">維修單管理</h3>
                    {!showRepairForm && (
                        <Button 
                            onClick={() => setShowRepairForm(true)} 
                            className="add-repair-btn"
                            disabled={warranty.verification_status !== 'Approved'}
                            title={warranty.verification_status !== 'Approved' ? 
                                '只有已批准的保養單可以創建維修單' : '創建新的維修單'}
                        >
                            創建維修單
                        </Button>
                    )}
                </div>

                {/* 維修單表單或列表 */}
                {showRepairForm ? (
                    <div className="warranty-repair-form">
                        <RepairForm 
                            token={token} 
                            user={user} 
                            warrantyId={warranty.id}
                            onSuccess={handleRepairFormSuccess}
                        />
                        <Button 
                            onClick={() => setShowRepairForm(false)} 
                            className="cancel-form-btn"
                        >
                            取消
                        </Button>
                    </div>
                ) : (
                    <div className="warranty-repair-list">
                        {repairsLoading ? (
                            <Loading text="正在加載維修單數據..." />
                        ) : repairsError ? (
                            <ErrorMessage message={repairsError} />
                        ) : repairs.length === 0 ? (
                            <div className="no-repairs-message">
                                此保養單暫無相關維修單
                            </div>
                        ) : (
                            <RepairList 
                                token={token} 
                                user={user} 
                                warrantyId={warranty.id}
                            />
                        )}
                    </div>
                )}
            </Card>

            {/* 管理員操作卡片 */}
            {renderAdminActions()}

            {/* 確認對話框 */}
            <ConfirmDialog
                isOpen={confirmDialog.isOpen}
                title={confirmDialog.title}
                message={confirmDialog.message}
                onConfirm={handleConfirmAction}
                onCancel={closeConfirmDialog}
            />
        </div>
    );
}

export default WarrantyDetail; 